/**
 * Table primitive — semantic <table> with default styles. The header
 * row, body rows, and pagination slot are composed by the caller so
 * page-level code stays expressive.
 *
 * Spec: PORTAL_MIGRATION_FROM_PSM_SSOT.md §4.1.2 (Phase 1 foundation).
 * Inline-style adaptation of PSM `components/ui/table.tsx`.
 */
import type { ReactNode, ThHTMLAttributes, TdHTMLAttributes } from 'react';

interface TableProps {
  children: ReactNode;
  'aria-label'?: string;
  /** Optional caption shown above the table for screen readers. */
  caption?: string;
}

export function Table({ children, caption, ...aria }: TableProps): JSX.Element {
  return (
    <table
      aria-label={aria['aria-label']}
      style={{
        width: '100%',
        borderCollapse: 'separate',
        borderSpacing: 0,
        fontSize: '0.875rem',
        color: '#111827',
      }}
    >
      {caption ? (
        <caption
          style={{
            captionSide: 'top',
            textAlign: 'left',
            padding: '0.5rem 0',
            color: '#4b5563',
          }}
        >
          {caption}
        </caption>
      ) : null}
      {children}
    </table>
  );
}

export function TableHead({ children }: { children: ReactNode }): JSX.Element {
  return (
    <thead
      style={{
        background: '#f9fafb',
        textAlign: 'left',
      }}
    >
      {children}
    </thead>
  );
}

export function TableBody({ children }: { children: ReactNode }): JSX.Element {
  return <tbody>{children}</tbody>;
}

export function TableRow({
  children,
  hover = false,
}: {
  children: ReactNode;
  /** When true, brighten the row on pointer hover. Default: false. */
  hover?: boolean;
}): JSX.Element {
  return (
    <tr
      style={{
        borderBottom: '1px solid #e5e7eb',
        ...(hover ? { cursor: 'pointer' } : null),
      }}
    >
      {children}
    </tr>
  );
}

export function TableHeaderCell({
  children,
  ...rest
}: ThHTMLAttributes<HTMLTableCellElement> & { children?: ReactNode }): JSX.Element {
  return (
    <th
      scope="col"
      style={{
        padding: '0.625rem 0.75rem',
        borderBottom: '1px solid #e5e7eb',
        fontWeight: 600,
        color: '#374151',
        whiteSpace: 'nowrap',
      }}
      {...rest}
    >
      {children}
    </th>
  );
}

export function TableCell({
  children,
  ...rest
}: TdHTMLAttributes<HTMLTableCellElement> & { children?: ReactNode }): JSX.Element {
  return (
    <td
      style={{
        padding: '0.625rem 0.75rem',
        borderBottom: '1px solid #f3f4f6',
        verticalAlign: 'top',
      }}
      {...rest}
    >
      {children}
    </td>
  );
}

/** Pagination slot — caller renders the page controls. Provides a
 *  consistent bordered footer below the table body. */
export function TablePagination({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0.625rem 0.75rem',
        borderTop: '1px solid #e5e7eb',
        background: '#fafafa',
        fontSize: '0.8125rem',
      }}
    >
      {children}
    </div>
  );
}
